import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { en } from './translations/en';
import { supabase } from '@/integrations/supabase/client';
import { SUPPORTED_LANGUAGES } from '@/lib/languages';

export const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map(l => ({
  code: l.code,
  name: `${l.nativeName} (${l.name})`,
}));

interface LanguageContextType {
  language: string;
  setLanguage: (code: string) => void;
  t: (key: string) => string;
  isTranslating: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const CACHE_PREFIX = 'ui_translations_';

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('preferred_language') || 'en';
  });
  const [translations, setTranslations] = useState<Record<string, string>>(en);
  const [isTranslating, setIsTranslating] = useState(false);

  const loadTranslations = useCallback(async (langCode: string) => {
    if (langCode === 'en') {
      setTranslations(en);
      return;
    }

    // Check localStorage cache
    const cached = localStorage.getItem(CACHE_PREFIX + langCode);
    if (cached) {
      try {
        setTranslations(JSON.parse(cached));
        return;
      } catch { /* fall through */ }
    }

    // Fetch from edge function
    setIsTranslating(true);
    try {
      const { data, error } = await supabase.functions.invoke('translate-ui', {
        body: { strings: en, targetLanguage: langCode },
      });

      if (error) throw error;

      if (data?.translations) {
        localStorage.setItem(CACHE_PREFIX + langCode, JSON.stringify(data.translations));
        setTranslations(data.translations);
      }
    } catch (err) {
      console.error('Failed to load translations:', err);
      // Fallback to English
      setTranslations(en);
    } finally {
      setIsTranslating(false);
    }
  }, []);

  const setLanguage = useCallback(async (code: string) => {
    setLanguageState(code);
    localStorage.setItem('preferred_language', code);
    await loadTranslations(code);

    // Save to profile if logged in
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await supabase
        .from('profiles')
        .update({ preferred_language: code } as any)
        .eq('id', session.user.id);
    }
  }, [loadTranslations]);

  // Load saved preference on mount
  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data } = await supabase
          .from('profiles')
          .select('preferred_language')
          .eq('id', session.user.id)
          .single();

        const dbLang = (data as any)?.preferred_language;
        if (dbLang && dbLang !== language) {
          setLanguageState(dbLang);
          localStorage.setItem('preferred_language', dbLang);
          await loadTranslations(dbLang);
          return;
        }
      }
      // Load translations for whatever language is set
      if (language !== 'en') {
        await loadTranslations(language);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const t = useCallback((key: string): string => {
    return translations[key] || en[key] || key;
  }, [translations]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isTranslating }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
